import { ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCallback, useContext, useMemo, useState } from 'react';

import { BrandContext } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import {
  STRIPE_NOT_INITIALIZED_ERROR_MESSAGE,
  STRIPE_PAYMENT_METHOD_CREATION_ERROR_MESSAGE,
} from 'lib/constants/paymentErrors';

const expressCheckoutOptions = {
  buttonType: { applePay: 'plain' },
  buttonHeight: 50,
  layout: { maxColumns: 1, maxRows: 1 },
  paymentMethods: {
    applePay: 'always',
    googlePay: 'never',
    link: 'never',
    paypal: 'never',
  },
  shippingAddressRequired: true,
  billingAddressRequired: true,
  phoneNumberRequired: true,
  emailRequired: true,
  allowedShippingCountries: ['US'],
};

const StripeExpressCheckout = ({ onReady, onComplete, onError, onClick }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { brand } = useContext(BrandContext);
  const [isLoading, setIsLoading] = useState(true);

  const options = useMemo(
    () => ({
      ...expressCheckoutOptions,
      business: { name: brandData(brand)?.displayName },
    }),
    [brand]
  );

  const handleReady = useCallback(
    event => {
      setIsLoading(false);
      const availablePaymentMethods = event.availablePaymentMethods;
      const isApplePayAvailable = availablePaymentMethods?.applePay;
      onReady?.({ isApplePayAvailable });
    },
    [onReady]
  );

  const handleClick = useCallback(
    async event => {
      try {
        const resolveOptions = await onClick?.();
        event.resolve(resolveOptions ?? {});
      } catch (error) {
        event.resolve({});
        onError?.(error);
      }
    },
    [onClick, onError]
  );

  const handleConfirm = useCallback(
    async event => {
      if (!stripe || !elements) {
        event.paymentFailed?.({ reason: 'fail' });
        onError?.(new Error(STRIPE_NOT_INITIALIZED_ERROR_MESSAGE));
        return;
      }

      const { billingDetails, shippingAddress, expressPaymentType } = event;

      try {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          event.paymentFailed?.({ reason: 'fail' });
          onError?.(submitError);
          return;
        }

        const { error, paymentMethod } = await stripe.createPaymentMethod({
          elements,
          params: {
            billing_details: billingDetails,
          },
        });

        if (error || !paymentMethod?.id) {
          const paymentMethodError =
            error || new Error(STRIPE_PAYMENT_METHOD_CREATION_ERROR_MESSAGE);
          event.paymentFailed?.({ reason: 'fail' });
          onError?.(paymentMethodError);
          return;
        }

        onComplete?.({
          paymentMethod,
          billingDetails,
          shippingAddress,
          expressPaymentType,
        });
      } catch (error) {
        event.paymentFailed?.({ reason: 'fail' });
        onError?.(error);
      }
    },
    [stripe, elements, onComplete, onError]
  );

  return (
    <div className="express-checkout-wrapper w-full">
      {isLoading && (
        <div className="h-[50px]">
          <span className="sr-only">Loading</span>
        </div>
      )}
      <ExpressCheckoutElement
        className="express-checkout-element w-full"
        options={options}
        onReady={handleReady}
        onConfirm={handleConfirm}
        onClick={handleClick}
      />
    </div>
  );
};

StripeExpressCheckout.propTypes = {
  onReady: PropTypes.func,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
  onClick: PropTypes.func,
};

export default StripeExpressCheckout;
